import React from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import { BASE_IMAGE_CDN_URL } from '../../cms/images';
import { SourceImage } from './styles';

const nextImageLoader = ({ src, width, quality }) => {
  const endUrl = src.match(/\/f\/.+\.(?:jpg|png|jpeg)$/);
  return `${BASE_IMAGE_CDN_URL}${width}x0${
    quality ? `/filters:quality(${quality})` : ''
  }${endUrl}`;
};

/**
 * Composes responsive images for an array of sources. Pass picture as
 * a prop to use this for art direction. Use a normal srcset if just
 * swapping identical images for diff. screen sizes
 *
 *
 * @prop src String: fallback image URL
 * @prop srcSet [object]: an array of sources
 *    e.g. [{ src: 'cat.jpg', pixelDensity: 1 }, { src: 'cat@2x.jpg'
 *    pixelDensity: 2 }]
 *    [{ srcSet: [{ srcs, above }], media: '(min-width: 640px)' }, ...] for picture
 * @prop picture Boolean: render a picture el instead of img srcset
 * @prop sizes String: Size of viewport the picture should occupy per
 * media query
 * @prop alt String: Alt text
 * @prop classNames: classnames compatible type ([String], String, {})
 */

const composeSrcSet = srcSet =>
  srcSet.reduce((acc, cur, idx) => {
    let str = cur.src;
    if (cur.width) {
      str += ` ${cur.width}w`;
    } else if (cur.pixelDensity) {
      str += ` ${cur.pixelDensity}x`;
    }
    if (idx !== srcSet.length - 1) {
      str += ', ';
    }
    return acc + str;
  }, '');

export const ResponsiveImage = ({
  src,
  alt = '',
  srcSet,
  picture = false,
  className,
  width,
  height,
  layout,
  objectFit,
}) => {
  if (picture) {
    return (
      <picture>
        {srcSet.map((src, i) => (
          <source
            media={src.media}
            key={i}
            srcSet={composeSrcSet(src.srcSet)}
            sizes={src.sizes}
          />
        ))}
        <SourceImage
          src={src}
          alt={alt}
          className={className}
          objectFit={objectFit}
        />
      </picture>
    );
  }

  // If our image is imported internally, don't run it through the
  // storyblok image loader
  const isStoryblokImage = src.match?.(/^https:\/\/(a|img2).storyblok.com/);

  // TODO(James) is this a terrible idea?
  // Storyblok doesn't provide dimensions with SVGs.
  // How can we support SVGs & non-SVGs from the CMS?
  if (isStoryblokImage && src.endsWith('.svg')) {
    return <SourceImage src={src} alt={alt} />;
  }

  // Static imports of SVGs can't be blurred
  const canBlur = typeof src === 'object' && !src.src.endsWith('.svg');

  return (
    <Image
      loader={isStoryblokImage ? nextImageLoader : undefined}
      src={src}
      alt={alt}
      className={className}
      width={width}
      height={height}
      layout={layout}
      objectFit={objectFit}
      placeholder={canBlur ? 'blur' : undefined}
    />
  );
};

ResponsiveImage.displayName = 'ResponsiveImage';

ResponsiveImage.propTypes = {
  src: PropTypes.oneOfType([
    PropTypes.string,
    // This is part of the shape provided by Nextjs's static image imports.
    PropTypes.shape({
      src: PropTypes.string.isRequired,
    }),
  ]).isRequired,
  alt: PropTypes.string,
  srcSet: PropTypes.arrayOf(PropTypes.object),
  picture: PropTypes.bool,
  sizes: PropTypes.string,
  className: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  layout: PropTypes.string,
  objectFit: PropTypes.oneOf(['contain', 'cover', 'none']),
};
